import { useState } from 'react'
import { useQuery, useMutation, type useQueryClient } from '@tanstack/react-query'
import { config, i18n, type RestaurantConfig } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { Button, Card, toast } from './ui/index.js'
import { I18nRunsPanel } from './I18nRunsPanel.js'
import { cn } from '../lib/utils.js'

// ─── Idiomas de tradução ──────────────────────────────────────────────────────

/**
 * Idiomas-alvo do cardápio. Salvar dispara na API o backfill: tradução LLM
 * do cardápio inteiro (produtos, ingredientes, variações, adicionais e
 * rótulos) para cada idioma marcado — fire-and-forget, alguns segundos.
 * Movido do Branding para Traduções (#336) — mesmas queryKeys/invalidações
 * para o painel de execuções da #330 continuar funcionando.
 */
export function TargetLocalesForm({
  cfg,
  qc,
}: {
  cfg: RestaurantConfig
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const [selected, setSelected] = useState<string[]>(
    cfg.target_locales.filter((l) => l !== cfg.primary_locale),
  )

  const mut = useMutation({
    mutationFn: (target_locales: string[]) => config.update({ target_locales }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      void qc.invalidateQueries({ queryKey: ['i18n-runs'] }) // backfill disparado
      toast.success(t('admin.branding.locales_saved_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // botão "Atualizar traduções": descarta rascunhos de IA e re-traduz o
  // cardápio deste restaurante a partir do idioma primário
  const refreshMut = useMutation({
    mutationFn: () => i18n.refresh(),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['i18n-runs'] }) // painel reage ao clique
      toast.success(t('admin.branding.retranslation_toast', { n: String(data.invalidated) }))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // catálogo de idiomas do banco (tabela locales) — sem lista hardcoded
  const { data: catalog } = useQuery({
    queryKey: ['i18n-locales'],
    queryFn: i18n.locales,
    staleTime: 5 * 60_000,
  })

  const toggle = (code: string) =>
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]))

  const options = (catalog ?? []).filter((l) => l.code !== cfg.primary_locale)
  const primaryName =
    (catalog ?? []).find((l) => l.code === cfg.primary_locale)?.native ?? cfg.primary_locale

  return (
    <Card
      title={t('admin.branding.target_locales_title')}
      description={t('admin.branding.target_locales_desc')}
    >
      <div className="space-y-4" data-testid="target-locales-form">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {options.map((l) => (
            <label
              key={l.code}
              lang={l.code}
              dir={l.code === 'ar' ? 'rtl' : 'ltr'}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer select-none',
                selected.includes(l.code)
                  ? 'border-orange-500 bg-orange-50 text-gray-900 font-medium'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
              )}
            >
              <input
                type="checkbox"
                className="accent-orange-600"
                checked={selected.includes(l.code)}
                onChange={() => toggle(l.code)}
              />
              {l.native}
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          {t('admin.branding.target_locales_hint', { primary: primaryName })}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button loading={mut.isPending} onClick={() => mut.mutate(selected)}>
            {t('admin.branding.save_target_locales')}
          </Button>
          <Button
            variant="secondary"
            loading={refreshMut.isPending}
            onClick={() => refreshMut.mutate()}
            data-testid="translations-refresh"
          >
            {t('admin.branding.refresh_translations')}
          </Button>
        </div>

        {/* status ao vivo — um item por idioma, atualiza enquanto traduz */}
        <div className="space-y-2" data-testid="i18n-runs-panel">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {t('admin.branding.translations_status')}
          </p>
          <I18nRunsPanel queryKey={['i18n-runs']} fetchRuns={i18n.runs} fetchLogs={i18n.runLogs} />
        </div>
      </div>
    </Card>
  )
}
